import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ExpenseReviewItem {
  id: string;
  code: string;
  targetType: 'BOOKING' | 'TOUR' | 'GENERAL';
  tourDetails: {
    tourNo: string;
    tourCode: string;
    tourName: string;
    country: string;
    travelDate: string | null;
    returnDate: string | null;
    requiredPax: number | null;
    tourTotalAmount: number | string;
  } | null;
  bookingCode: string;
  customerName: string;
  tripName: string;
  travelDate: string | null;
  travelerCount: number;
  bookingTotal: number | string;
  title: string;
  expenseNameSnapshot: string;
  paymentAccountSource: 'REFERENCE_FILE' | 'OTHER_BANK_ACCOUNT' | null;
  bankName: string | null;
  accountNumber: string | null;
  requester: string;
  purchaseOrderFileName: string;
  attachmentUrl: string | null;
  receiptUrl: string;
  receiptPath: string | null;
  receiptFileName: string;
  financialAdjustments: Array<{
    id: string;
    adjustmentNo: string;
    targetType: 'BOOKING' | 'TOUR';
    tourPriceDelta: number;
    expenseBudgetDelta: number;
    customerChargeDelta: number;
    companyAdvanceAmount: number;
    advanceAttachmentPath: string | null;
    reason: string;
    status: 'DRAFT' | 'SUPERVISOR_REVIEW' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'DELETED';
    requestedByName: string;
    createdAt: string;
  }>;
  amount: number | string;
  status: 'WAITING_PAYMENT' | 'ACCOUNTING_REVIEW_RECEIPT';
  submittedAt: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class ExpenseReviewService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/expenses/accounting-review`;

  async getPending(): Promise<ExpenseReviewItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ expenses: ExpenseReviewItem[] }>(this.apiUrl, { withCredentials: true }),
    );
    return response.expenses;
  }

  async approve(
    id: string,
    expectedStatus: ExpenseReviewItem['status'],
    expectedReceiptPath: string | null,
    receiptUrl?: string,
    transferDate?: string,
    transferTime?: string,
    withholdingTaxAmount = 0,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}/${id}/approve`, {
        expectedStatus,
        expectedReceiptPath,
        ...(receiptUrl ? { receiptUrl } : {}),
        ...(transferDate ? { transferDate } : {}),
        ...(transferTime ? { transferTime } : {}),
        withholdingTaxAmount,
      }, {
        withCredentials: true,
      }),
    );
  }

  async reject(id: string, expectedStatus: ExpenseReviewItem['status'], expectedReceiptPath: string | null): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}/${id}/reject`, { expectedStatus, expectedReceiptPath }, { withCredentials: true }),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl))
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    return fileUrl;
  }
}
